import {
  Table,
  Row,
  Col,
  Container,
  Button,
  Form,
  Image,
} from "react-bootstrap";
import { useState, Fragment } from "react";
import { ModalMessage, LoadingSpinner } from "../../components";
import {
  adminAddCategoryImageApi,
  adminCreateCategoryApi,
  adminDeleteCategoryApi,
  adminUpdateCategoryApi,
} from "../../api/categoriesApi.js";
import { ScrollToTop, getStorageToken, useLogout } from "../../utilities";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteCloudinaryImagesApi,
  uploadCloudinaryImagesApi,
} from "../../api/cloudinaryApi";
import { loadCategories } from "../../redux/categoriesSlice";
import convertToBlob from "../../utilities/convertToBlob";

export default function AdminCategoriesList() {
  // displays a list of all available categories
  // admin can add/edit/delete categories

  const headers = ["Name"];
  const types = { DEFAULT: "", ADDING: "adding", EDITING: "editing" };

  const logout = useLogout();
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.categories);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [addingEditing, setAddingEditing] = useState(types.DEFAULT); //"editing" / "adding"
  const [addEditCategory, setAddEditCategory] = useState({
    name: "",
    parent: "",
    image: null,
  });
  const [showAlertDelete, setShowAlertDelete] = useState(false);

  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [deleteImagePublicId, setDeleteImagePublicId] = useState(null);
  const { stayLoggedIn } = useSelector((state) => state.user);
  

 
  const saveCategory = async () => {
    setIsLoading(true);
    const abortController = new AbortController();
    try {
      setIsLoadingImages(true);
      const token = getStorageToken(stayLoggedIn);
      let res;
      let categoryData = {
        name: addEditCategory.name,
        _id: addEditCategory._id,
        parent: addEditCategory.parent,
      };
      if (addingEditing === types.ADDING) {
        // creating a new category
        res = await adminCreateCategoryApi(
          token,
          categoryData,
          abortController.signal
        );
      } else if (addingEditing === types.EDITING) {
          if (addEditCategory?.image?.public_id) {
            //dont need to upload image (ie image didn't change)
            categoryData.image = addEditCategory.image;
          }
          res = await adminUpdateCategoryApi(
            token,
            categoryData,
            abortController.signal
          );
        }
        if (res) {
        // handle image upload if applicable
        if (addEditCategory?.image && !addEditCategory.image?.public_id) {
          // new image, not yet on cloudinary
          await handleImageUpload(res._id);
        } else {
          dispatch(loadCategories());
        }
        // see if need to delete images
        if (deleteImagePublicId) {
          deleteUploadedImage(deleteImagePublicId);
        }
        setSuccessMsg("Category saved successfully");
        setAddEditCategory({ name: "", image: null });
        setAddingEditing(types.DEFAULT);
        setIsLoadingImages(false);
        setIsLoading(false);

      }
    } catch (error) {
      setIsLoadingImages(false);
      setIsLoading(false);
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      } else if (error?.status === 409) {
        setErrorMsg(
          "A category with this name already exists. Please try a different name."
        );
      } else {
        setErrorMsg("Could not save the category. Please try again later.");
      }
    }
  };

  const handleImageUpload = async (categoryId) => {
    //when creating new product/updating product, do image upload in background
    try {
      const abortController = new AbortController();
      const token = getStorageToken(stayLoggedIn);
      const images = await uploadCloudinaryImagesApi(
        token,
        [addEditCategory.image],
        abortController.signal
      );
      const uploadedImage = images[0];
      await adminAddCategoryImageApi(token, categoryId, {
        image: uploadedImage,
      });
      dispatch(loadCategories());
    } catch (error) {
      setErrorMsg(
        "Image upload failed. Please try again later, or contact our support team for further assistance."
      );
    }
  };

  const handleCancelClick = () => {
    setAddEditCategory({ name: "", image: null });
    setAddingEditing(types.DEFAULT);
  };

  const handleEdit = (category) => {
    setAddingEditing(types.EDITING);
    setAddEditCategory(category);
  };

   
  const handleConfirmDelete = async () => {
      try {
        const token = getStorageToken(stayLoggedIn);
        //delete category
        const abortController = new AbortController();
        const { imagePublicId } = await adminDeleteCategoryApi(
          token,
          showAlertDelete?.payload,
          abortController.signal
        );
        dispatch(loadCategories());
        deleteCloudinaryImagesApi(token, [imagePublicId]);
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        } else if (error?.status === 409) {
          setErrorMsg(
            "This category is referenced by one or more products. Please remove any  references before deleting this category."
          );
        } else {
          setErrorMsg("Could not delete category. Please try again later.");
        }
      }
  };

  const deleteUploadedImage = async (publicId) => {
    if (!publicId) {
      return;
    }
    try {
      const token = getStorageToken(stayLoggedIn);
      await deleteCloudinaryImagesApi(token, [publicId]);
    } catch (error) {
      setErrorMsg(
        "Image could not be deleted from the server. Please contact our support team for assistance."
      );
    }
  };

  const renderCategoryTree = (categories) => {
    return categories.map((category, idx) => {
      return (
        <Fragment key={category?._id}>
          <tr>
            <td>{idx + 1}</td>
            <td>{category?.fullPath}</td>
            <td className="d-flex justify-content-end">
              {/* show buttons */}
              <Button
                disabled={addingEditing}
                className="btn-sm"
                variant="warning"
                onClick={() => handleEdit(category)}
              >
                <i className="bi bi-pencil-square"></i>
              </Button>
              {"\u00A0\u00A0\u00A0"}
              <Button
                disabled={addingEditing}
                className="btn-sm "
                variant="danger"
                onClick={async () => setShowAlertDelete({show: true, payload: category._id})}
              >
                <i className="bi bi-trash3-fill"></i>
              </Button>{" "}
              {"\u00A0\u00A0\u00A0"}
            </td>
          </tr>
          {category.children && category.children.length > 0 && (
            <>{renderCategoryTree(category.children)}</>
          )}
        </Fragment>
      );
    });
  };

  const renderCategoryDropdown = (categories) => {
    return categories.map((category, idx) => {
      if (category?._id !== addEditCategory._id) {
        return (
          <Fragment key={category?._id}>
            <option value={category._id}>{category?.fullPath}</option>
            {category.children && category.children.length > 0 && (
              <>{renderCategoryDropdown(category.children)}</>
            )}
          </Fragment>
        );
      } else {
        return null
      }
    });
  };

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      {addingEditing && <ScrollToTop />}
      <h1>Categories</h1>
      <Button
        variant="primary"
        // className="mt-2"
        onClick={() => setAddingEditing(types.ADDING)}
        disabled={addingEditing}
      >
        Add Category
      </Button>

      {addingEditing && (
        <Form
          onSubmit={async (event) => {
            event.preventDefault();
            await saveCategory();
          }}
          className="mt-4"
          style={{
            border: "#D3D3D3 2px solid",
            padding: "30px 0px",
            borderRadius: "10px",
          }}
        >
          <Row className="mx-3">
            <Col sm={12} className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                required={true}
                value={addEditCategory.name}
                onChange={(event) =>
                  setAddEditCategory((prevCategory) => ( 
                   { ...prevCategory,
                    name: event.target.value,
                  }))
                  }
              />
            </Col>

            <Col sm={12} className="mb-2">
              <Form.Label>Parent Category</Form.Label>
              <Form.Select
                name="category"
                value={addEditCategory?.parent || ""}
                onChange={(event) =>
                  setAddEditCategory((prevCategory) => ({
                    ...prevCategory,
                    parent: event.target.value })
                  )
                }
              >
                {/* only show options that don't have addEdit one as parent to avoid circular references */}
                <option key={-1} value={""}>
                  {"None"}
                </option>
                {categories && renderCategoryDropdown(categories)}
              </Form.Select>
            </Col>
            <Col sm={12} className="mb-2">
              <Form.Label>Image (not required for child categories)</Form.Label>

              <Form.Control
                name="images"
                type="file"
                accept="image/*"
                onChange={async (event) => {
                  let newImage = event.target.files[0];
                  // if have cloudinary image in addEditCategory when choosing a new image,
                  // delete it from cloudinary (setDeleteImage to that image, don't delete right away on cancel won't work). if it's not a cloudinary image, no deletion required
                  if (addEditCategory?.image?.public_id) {
                    setDeleteImagePublicId(addEditCategory.image.public_id);
                  }
                  setAddEditCategory((prevCategory) => ({
                    ...prevCategory,
                    image: newImage,
                  }))
                }}
              />
            </Col>
            <Col>
              <div style={{ position: "relative" }} className="my-3">
                {addEditCategory?.image &&
                  !addEditCategory?.image?.public_id && (
                    <Image
                      fluid
                      style={{ height: "100px" }}
                      src={URL.createObjectURL(
                        convertToBlob([addEditCategory?.image])[0]
                      )}
                    ></Image>
                  )}
                {addEditCategory?.image &&
                  addEditCategory?.image?.public_id && (
                    <Image fluid src={addEditCategory.image?.url}></Image>
                  )}
              </div>
            </Col>
          </Row>
          <Row className="ms-3 mb-1">
            {(
              !addEditCategory?.name ||
              (!addEditCategory.parent && !addEditCategory?.image) 
            ) && (
              <Col style={{ color: "darkred" }} className="ms-1">
                <i>Please fill out all required fields.</i>
              </Col>
            )}
          </Row>
          <Row className="ms-3">
            <Col>
              <Button
                type="submit"
                variant="primary"
                className="me-2"
                disabled={
                  (
                    !addEditCategory?.name ||
              (!addEditCategory.parent && !addEditCategory?.image) 
                  )
                }
              >
                {isLoadingImages && (
                  <LoadingSpinner button={true} variant="light" />
                )}{" "}
                {addingEditing === types.ADD ? "Add" : "Save"}
              </Button>
              <Button variant="danger" onClick={handleCancelClick}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      )}
      {isLoading && <LoadingSpinner />}
      {categories && (
        <Table
          className="mt-4"
          rounded="md"
          hover
          responsive
          style={{ borderLeft: "none", borderRight: "none" }}
        >
          <thead>
            <tr>
              <th>#</th>
              {headers?.map((header, idx) => {
                return <th key={idx}>{header}</th>;
              })}
              <th></th>
            </tr>
          </thead>
          <tbody>{categories && renderCategoryTree(categories)}</tbody>
        </Table>
      )}

                  {/* alert message */}
                  {showAlertDelete?.show && 
      <ModalMessage
          message= "Are you sure you want to delete this category?"
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirmDelete}
          setShowAlertMsg={setShowAlertDelete}
        />}
    </Container>
  );
}
